import { eq } from "drizzle-orm";
import {
  list,
  list_group,
  list_group_item,
  type PowerSyncDrizzleDB,
  todo,
} from "@listto/powersync";
import { nowISO } from "../utils/date-helpers";
import { generateUUID } from "../utils/uuid";

export async function createListMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  name: string,
  opts?: {
    color?: string;
    icon?: string;
    groupId?: string;
    themeId?: string | null;
    isBlur?: boolean;
  },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();

  await db.transaction(async (trx) => {
    await trx.insert(list).values({
      id,
      name: name.trim(),
      color: opts?.color ?? null,
      icon: opts?.icon ?? null,
      user_id: userId,
      theme_id: opts?.themeId ?? null,
      is_blur: opts?.isBlur === false ? 0 : 1,
      created_at: now,
      updated_at: now,
    });

    if (opts?.groupId) {
      await trx.insert(list_group_item).values({
        id: generateUUID(),
        list_id: id,
        group_id: opts.groupId,
        user_id: userId,
        added_at: now,
      });
    }
  });

  return id;
}

export async function updateListMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{
    name: string;
    color: string | null;
    icon: string | null;
    theme_id: string | null;
    is_blur: number;
  }>,
): Promise<void> {
  const finalUpdates = { ...updates };
  if (finalUpdates.name !== undefined) {
    finalUpdates.name = finalUpdates.name.trim();
  }

  await db
    .update(list)
    .set({ ...finalUpdates, updated_at: nowISO() })
    .where(eq(list.id, id));
}

export async function createListGroupMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  name: string,
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(list_group).values({
    id,
    name: name.trim(),
    user_id: userId,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateListGroupMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{ name: string }>,
): Promise<void> {
  const finalUpdates = { ...updates };
  if (finalUpdates.name !== undefined) {
    finalUpdates.name = finalUpdates.name.trim();
  }

  await db
    .update(list_group)
    .set({ ...finalUpdates, updated_at: nowISO() })
    .where(eq(list_group.id, id));
}

export async function assignListToGroupMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  listId: string,
  groupId: string | null,
): Promise<void> {
  await db.transaction(async (trx) => {
    if (groupId) {
      await trx.delete(list_group_item).where(eq(list_group_item.list_id, listId));
      await trx.insert(list_group_item).values({
        id: generateUUID(),
        list_id: listId,
        group_id: groupId,
        user_id: userId,
        added_at: nowISO(),
      });
    } else {
      await trx.delete(list_group_item).where(eq(list_group_item.list_id, listId));
    }
  });
}

export async function deleteListOptimisticMutation(
  db: PowerSyncDrizzleDB,
  id: string,
): Promise<void> {
  await db.transaction(async (trx) => {
    await trx.update(todo).set({ list_id: null }).where(eq(todo.list_id, id));
    await trx.delete(list_group_item).where(eq(list_group_item.list_id, id));
    await trx.delete(list).where(eq(list.id, id));
  });
}

export async function deleteListGroupOptimisticMutation(
  db: PowerSyncDrizzleDB,
  id: string,
): Promise<void> {
  await db.transaction(async (trx) => {
    await trx.delete(list_group_item).where(eq(list_group_item.group_id, id));
    await trx.delete(list_group).where(eq(list_group.id, id));
  });
}
